import {
  ButtonGroup,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  faCheck,
  faCopy,
  faEllipsis,
  faPencilAlt,
  faShareFromSquare,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRef } from 'react';
import { TFunction } from 'i18next';
import { useProtanProjectSteps } from '../common/ProtanProjectStepsProvider';
import { useProjectHistory } from '../project/project-history/hooks/useProjectHistory';
import {
  getHistoricalOrderFields,
  getHistoricalOrderLabelDiffs,
  useDiffing,
} from '../project/project-history/hooks/useDiffing';
import ProtanUploadModal from './ProtanUploadModal';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from 'src/components/common/Panel';

import OrderInfo from 'src/components/orderContractor/OrderInfo';
import OrderStatusOverview from 'src/components/project/orderStatusOverview/OrderStatusOverview';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { useSelectedOrder } from 'src/hooks/selected-order';
import {
  ProjectDeepFragment,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
  OrderStatus,
  EventType,
  OrderDeepFragment,
} from 'src/gql/graphql';
import { ResolvedDbIdsReturn } from 'src/hooks/order';
import CopyOrderModal from 'src/components/orderContractor/CopyOrderModal';
import OrderSharingModal from 'src/components/orderContractor/OrderSharingModal';
import { DiffText } from 'src/components/project/project-history/DiffText';
import StyledKeyValues from 'src/components/common/StyledKeyValues';
import UserBadge from 'src/components/common/UserBadge';
import OrderBadge from 'src/components/common/OrderBadge';

interface StatusPanelProps {
  project: ProjectDeepFragment;
  resolvedDbids: ResolvedDbIdsReturn;
  isContractor: boolean;
  isProtan: boolean;
}
/* eslint-disable complexity */
export function StatusPanel({
  project,
  resolvedDbids,
  isContractor,
  isProtan,
}: StatusPanelProps) {
  // Grab the order and orderId from context and state. -> Maybe I only need the order itself let's see how that goes
  const { selectedOrder } = useSelectedOrder();
  const { orderId } = useParams();
  const { t } = useTranslation(['project', 'protan']);
  const order = selectedOrder?.order;
  const orderInfoRef = useRef<HTMLDivElement>(null);

  const { isOpen: showProtanUploadModal, onClose: closeProtanUploadModal } =
    useDisclosure({ defaultIsOpen: true });
  const { projectSteps } = useProtanProjectSteps();
  const aiAnalysisStep = projectSteps?.find(
    (step) => step.step === ProtanProjectStepEnum.AiAnalysis
  );
  const protanFilesUploaded =
    aiAnalysisStep &&
    aiAnalysisStep?.status !== ProtanProjectStepStatusEnum.NotStarted;

  const {
    isOpen: editOrder,
    onOpen: startEdit,
    onClose: stopEdit,
  } = useDisclosure();

  const {
    isOpen: showCopyModal,
    onOpen: openCopyModal,
    onClose: closeCopyModal,
  } = useDisclosure();

  const {
    isOpen: showShareModal,
    onOpen: openShareModal,
    onClose: closeShareModal,
  } = useDisclosure();

  const { isHistoryDrawerOpen, diff, resolvedStates } = useProjectHistory();

  if (!order || !orderId) {
    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>{t('project-status-panel.title')}</PanelTitle>
        </PanelHeader>
        <PanelContent>
          <OrderStatusOverview
            project={project}
            resolvedDbIds={resolvedDbids}
          />
        </PanelContent>
      </Panel>
    );
  }

  return (
    <>
      {isProtan && !protanFilesUploaded ? (
        <ProtanUploadModal
          order={order}
          isOpen={showProtanUploadModal && isProtan}
          onClose={closeProtanUploadModal}
          projectId={project.id}
        />
      ) : null}
      <Panel
        aria-label="Table information"
        resizeable
        panelContentRef={orderInfoRef}
      >
        <PanelHeader hasNoMarginBottom>
          <PanelTitle>{t('table-info-panel-header')}</PanelTitle>
          {isContractor && !isHistoryDrawerOpen ? (
            <PanelActionButtons>
              <ButtonGroup spacing={1}>
                {!editOrder ? (
                  <Menu isLazy>
                    {({ isOpen }) => (
                      <>
                        <Tooltip
                          label={t('table-settings-menu-tooltip')}
                          openDelay={400}
                          isDisabled={isOpen}
                        >
                          <MenuButton
                            as={IconButton}
                            aria-label={`Open table menu for ${order.name}`}
                            size="xs"
                            colorScheme="gray"
                            icon={<SparkelIcon icon={faEllipsis} />}
                          />
                        </Tooltip>
                        <Portal>
                          <MenuList zIndex={99}>
                            <MenuItem
                              aria-label={`Edit table ${order.name}`}
                              onClick={startEdit}
                              icon={<SparkelIcon icon={faPencilAlt} />}
                            >
                              {t('table-menu-item.edit-table')}
                            </MenuItem>
                            <Divider />
                            <MenuItem
                              onClick={openCopyModal}
                              icon={<SparkelIcon icon={faCopy} />}
                            >
                              {t('table-menu-item.duplicate-table')}
                            </MenuItem>
                            <Divider />
                            <MenuItem
                              onClick={openShareModal}
                              icon={<SparkelIcon icon={faShareFromSquare} />}
                            >
                              {t('table-menu-item.share-table')}
                            </MenuItem>
                          </MenuList>
                        </Portal>
                      </>
                    )}
                  </Menu>
                ) : (
                  <>
                    <Tooltip label={t('edit-table.button.cancel')}>
                      <IconButton
                        aria-label="Cancel edit"
                        colorScheme="gray"
                        size="xs"
                        onClick={stopEdit}
                        isRound
                        icon={<SparkelIcon icon={faTimes} />}
                      ></IconButton>
                    </Tooltip>
                    <Tooltip label={t('edit-table.button.save')}>
                      <IconButton
                        key="save_order_button"
                        aria-label="Save table"
                        size="xs"
                        type="submit"
                        form="edit-order-form"
                        isRound
                        icon={
                          <SparkelIcon
                            icon={faCheck}
                            color={['gray.50', 'gray.700']}
                          />
                        }
                      ></IconButton>
                    </Tooltip>
                  </>
                )}
              </ButtonGroup>
            </PanelActionButtons>
          ) : null}
        </PanelHeader>
        <PanelContent ref={orderInfoRef} paddingTop={0}>
          {isHistoryDrawerOpen ? (
            <StatusPanelDiff {...{ order, resolvedStates, diff, t }} />
          ) : (
            <OrderInfo
              editOrder={editOrder && !isHistoryDrawerOpen}
              stopEdit={stopEdit}
              order={order}
              project={project}
            />
          )}
        </PanelContent>
      </Panel>
      <OrderSharingModal
        defaultOrder={{ id: order.id, name: order.name }}
        orders={project.orders}
        isOpen={showShareModal}
        onClose={closeShareModal}
      />
      <CopyOrderModal
        order={order}
        modalIsOpen={showCopyModal}
        closeModal={closeCopyModal}
      />
    </>
  );
}

type StatusPanelDiffProps = {
  resolvedStates: [any, any] | null;
  diff: Record<string, any> | null;
  order: OrderDeepFragment;
  t: TFunction<[string, string], undefined>;
};

function StatusPanelDiff({
  resolvedStates,
  diff,
  order,
  t,
}: StatusPanelDiffProps) {
  const { getBackgroundColor } = useDiffing();

  if (!resolvedStates || !diff) return null;
  const diffs = getHistoricalOrderFields(
    resolvedStates,
    (diff as Record<string, unknown>) || {},
    order.id
  );
  const toOrderStatus = (s?: string): OrderStatus | undefined => {
    if (!s) return undefined;
    const values = Object.values(OrderStatus) as string[];
    return values.includes(s) ? (s as unknown as OrderStatus) : undefined;
  };
  // render statuses using OrderBadge

  const keyValues: {
    key: string;
    value: React.ReactElement | string;
    containerBg?: string;
  }[] = [
    {
      key: t('table-name'),
      value: <DiffText oldValue={diffs.name.old} newValue={diffs.name.new} />,
      containerBg:
        diffs.name.changed && diffs.name.old !== diffs.name.new
          ? getBackgroundColor(EventType.Modified)
          : undefined,
    },
    {
      key: t('table-created-by'),
      value: <UserBadge username={order.createdBy ?? ''} />,
    },
  ];

  if (!order.isAggregatedOrder) {
    const oldEnum = toOrderStatus(diffs.status.old);
    const newEnum = toOrderStatus(diffs.status.new);
    const statusChanged = Boolean(
      diffs.status.changed && diffs.status.old !== diffs.status.new
    );
    keyValues.push({
      key: t('table-status'),
      value:
        statusChanged && oldEnum && newEnum ? (
          <span>
            <OrderBadge orderStatus={oldEnum} />
            <span> → </span>
            <OrderBadge orderStatus={newEnum} />
          </span>
        ) : (
          <span>
            <OrderBadge orderStatus={newEnum || oldEnum || order.status} />
          </span>
        ),
      containerBg: statusChanged
        ? getBackgroundColor(EventType.Modified)
        : undefined,
    });
  }

  const labelDiffs = getHistoricalOrderLabelDiffs(resolvedStates, order.id);
  labelDiffs.forEach((ld) => {
    keyValues.push({
      key: ld.key,
      value: <DiffText oldValue={ld.old} newValue={ld.new} />,
      containerBg:
        (ld.old ?? undefined) !== (ld.new ?? undefined)
          ? getBackgroundColor(EventType.Modified)
          : undefined,
    });
  });
  return <StyledKeyValues keyValues={keyValues} />;
}
